import Chart from "react-apexcharts";
import { useContext, useEffect } from "react";
import { ApexOptions } from "apexcharts";
import { CustomState, CustomStateHook } from "../../../../Hooks/State.hook";
import { MessagesPerHourAndDayResponse } from "./types";
import { customRequest } from "../../../../services/api";
import LoadingSpinner from "../../../../components/LoadingSpinner";
import { LoginContext } from "../../../../contexts/global/LoginContext";
import StyledMessagesByHourAndDay from "./style";
import MultiSelect from "../../../../components/MultiSelect";
import cssVars from "../../../../styles/cssVariables.vars";
import { Sector } from "../../../../interfaces/RunningAttendances";
import { UserData } from "../../../../interfaces/Users.interfaces";
import { RegistrationButton } from "../../../../styles/registrationButton";
import InputDateRange from "../../../../components/InputDateRange";

const generateDataForDay = (day: string) => {
    const hours = [
        '00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00',
        '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00',
        '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'
    ];

    return {
        name: day,
        data: hours.map((hour) => ({ x: hour, y: 0 }))
    };
}

function MessagesByHourAndDay() {
    const days = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
    const generatedSeries = [...days.map(day => generateDataForDay(day))];

    const { darkMode } = useContext(LoginContext);

    const options: ApexOptions = {
        chart: {
            type: 'heatmap',
        },
        dataLabels: {
            enabled: true,
            style: {
                colors: ["var(--color-grey-1)"]
            },
        },
        legend: {
            show: false,
        },
        theme: {
            mode: darkMode ? "dark" : "light"
        },
        noData: {
            text: "Sem dados"
        },
        tooltip: {
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const day = w.globals.seriesNames[seriesIndex];
                const hour = w.globals.labels[dataPointIndex];
                const value = series[seriesIndex][dataPointIndex];

                return `
                <div style="padding: 5px; color: white; background-color: #333;">
                    ${day} ${hour}<br>
                    ${Number(value)}
                </div>`;
            }
        }
    };

    const series = CustomState<ApexAxisChartSeries>(generatedSeries);
    const isLoading = CustomState<boolean>(false);
    const loaded = CustomState<boolean>(false);
    const sectors = CustomState<Sector[]>([]);
    const operators = CustomState<UserData[]>([]);
    const selectedSectors = CustomState<number[]>([]);
    const selectedOperators = CustomState<number[]>([]);
    const selectedRangeDate = CustomState<{ min: Date | null, max: Date | null }>({ min: null, max: null });
    const filteredOperators = operators.value.filter(o => {
        if (selectedSectors.value.length === 0) {
            return o
        } else {
            return selectedSectors.value.includes(o.SETOR);
        }
    })

    function loadData(
        SETORES: string,
        OPERADORES: string,
        PERIODO: { min: Date | null, max: Date | null },
        seriesState: CustomStateHook<ApexAxisChartSeries>,
        loadingState: CustomStateHook<boolean>,
        loadedState: CustomStateHook<boolean>
    ) {
        customRequest<MessagesPerHourAndDayResponse, null>({
            method: "get",
            endpoint: "/dashboard/messages-per-hour-day",
            params: {
                SETORES,
                OPERADORES,
                MIN_DATE: PERIODO.min ? PERIODO.min.getTime() : null,
                MAX_DATE: PERIODO.max ? PERIODO.max.getTime() : null,
            },
            onSuccess: (data) => {
                const prev = generatedSeries;

                data.messagesPerHourDay.forEach(h => {
                    const correctedHour = h.hour - 3;

                    const correctedHour24 = (correctedHour + 24) % 24;
                    (prev[h.weekDay - 1].data[correctedHour24])["y"] = Number(h.messagesCount);
                });

                seriesState.set(prev);
                loadedState.set(true);
            },
            loadingState,
        });
    }

    function onFilter() {
        loadData(
            selectedSectors.value.length ? selectedSectors.value.join(",") : "*",
            selectedOperators.value.length ? selectedOperators.value.join(",") : "*",
            selectedRangeDate.value,
            series, isLoading, loaded
        );
    }

    useEffect(() => {
        loadData("*", "*", { min: null, max: null }, series, isLoading, loaded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        customRequest<UserData[], null>({
            method: "get",
            endpoint: "/operators",
            onSuccess: (data) => {
                const setores = data.map(o => ({
                    CODIGO: o.SETOR, NOME: o.SETOR_NOME,
                    WHATSAPP_NUMERO: o.SETOR_NUMERO,
                    WHATSAPP_NUMERO_ID: o.SETOR_NUMERO,
                    IS_SALES: true
                })).filter(o => typeof o.CODIGO === "number")

                const set = new Set<number>();
                const setoresUnicos = setores.filter(setor => {
                    if (!set.has(setor.CODIGO)) {
                        set.add(setor.CODIGO);
                        return true;
                    }
                    return false;
                });

                sectors.set(setoresUnicos);
                operators.set(data);
            }
        })
    }, []);

    return (
        <StyledMessagesByHourAndDay>
            <div className="filters">
                <div>
                    <InputDateRange
                        $fontSize={0.875}
                        $fontWeight={500}
                        $padding={[0.5, 0.5]}
                        $width="100%"
                        $color={cssVars.colorGrey[4]}
                        $focusColor={cssVars.colorGrey[2]}
                        title="Período"
                        onChange={(v) => selectedRangeDate.set(v)}
                    />
                </div>
                <div>
                    <MultiSelect<number>
                        $fontSize={0.875}
                        $fontWeight={500}
                        $padding={[0.5, 0.5]}
                        $width="100%"
                        $color={cssVars.colorGrey[4]}
                        options={sectors.value.map(s => ({ name: s.NOME, value: s.CODIGO, key: s.CODIGO }))}
                        onChange={(v) => selectedSectors.set(v)}
                        placeholder="Selecione o(s) setor(es)"
                    />
                    <MultiSelect<number>
                        $fontSize={0.875}
                        $fontWeight={500}
                        $padding={[0.5, 0.5]}
                        $width="100%"
                        $color={cssVars.colorGrey[4]}
                        options={filteredOperators.map(s => ({ name: s.NOME, value: s.CODIGO, key: s.CODIGO }))}
                        onChange={(v) => selectedOperators.set(v)}
                        placeholder="Selecione o(s) operador(es)"
                    />
                    <RegistrationButton onClick={onFilter}>
                        Filtrar
                    </RegistrationButton>
                </div>
            </div>
            {
                (loaded.value && series.value) ?
                    <Chart
                        options={options}
                        series={series.value}
                        type="heatmap"
                        width="100%"
                        height="96%"
                    />
                    :
                    <>
                    </>
            }
            {
                isLoading.value && <LoadingSpinner />
            }
        </StyledMessagesByHourAndDay>
    );
};

export default MessagesByHourAndDay;
